import React from 'react';
import { Search, FileCheck, Plane, CheckCircle } from 'lucide-react';

const ProcessTimeline = () => {
  const steps = [
    {
      number: "01",
      icon: Search,
      title: "Discover Your Path",
      subtitle: "Free Personalized Consultation",
      description: "We start by understanding your goals, background, and preferences. Our experts analyze your profile and recommend the best countries, universities, and courses tailored just for you.",
      highlights: [
        "One-on-one counseling session",
        "Profile evaluation & career guidance",
        "University & course recommendations"
      ],
      color: "from-[#1565C0] to-[#0D47A1]",
      bgColor: "bg-blue-50"
    },
    {
      number: "02",
      icon: FileCheck,
      title: "Prepare & Apply",
      subtitle: "Complete Application Support",
      description: "We handle the paperwork, applications, and visa documentation. From university admissions to visa filing, we ensure everything is accurate, complete, and submitted on time.",
      highlights: [
        "Document preparation & review",
        "University application assistance",
        "Visa application & interview prep"
      ],
      color: "from-[#FF7043] to-[#F4511E]",
      bgColor: "bg-orange-50"
    },
    {
      number: "03",
      icon: Plane,
      title: "Launch Your Journey",
      subtitle: "Ongoing Support Until You Land",
      description: "Even after visa approval, we're with you. Get help with accommodation, travel arrangements, pre-departure briefings, and settling into your new life abroad.",
      highlights: [
        "Pre-departure orientation",
        "Accommodation assistance",
        "Post-landing support"
      ],
      color: "from-[#8BC34A] to-[#7CB342]",
      bgColor: "bg-green-50"
    }
  ];

  return (
    <section id="process" className="py-20 bg-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#1565C0] rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#FF7043] rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-block mb-4">
            <span className="bg-gradient-to-r from-[#1565C0] to-[#0D47A1] text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg">
              Our Proven Process
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Your Success in 3 Simple Steps
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We've refined our process over 15 years to make your study abroad journey smooth, stress-free, and successful.
          </p>
        </div>

        {/* Timeline */}
        <div className="max-w-6xl mx-auto">
          {steps.map((step, index) => {
            const IconComponent = step.icon;
            const isEven = index % 2 === 0;
            
            return (
              <div key={index} className="relative mb-20 last:mb-0">
                {/* Connecting Line */}
                {index < steps.length - 1 && (
                  <div className={`absolute left-1/2 top-32 w-1 h-40 bg-gradient-to-b ${step.color} hidden lg:block transform -translate-x-1/2 opacity-30`}></div>
                )}

                <div className={`flex flex-col lg:flex-row items-center gap-8 ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                  {/* Content Side */}
                  <div className={`flex-1 ${isEven ? 'lg:text-right' : 'lg:text-left'}`}>
                    <div className={`inline-block ${step.bgColor} rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2`}>
                      <div className="mb-4">
                        <span className={`text-6xl font-bold bg-gradient-to-r ${step.color} bg-clip-text text-transparent`}>
                          {step.number}
                        </span>
                      </div>
                      <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                        {step.title}
                      </h3>
                      <p className={`text-lg font-semibold bg-gradient-to-r ${step.color} bg-clip-text text-transparent mb-4`}>
                        {step.subtitle}
                      </p>
                      <p className="text-gray-700 leading-relaxed mb-6">
                        {step.description}
                      </p>
                      <div className="space-y-3">
                        {step.highlights.map((highlight, idx) => (
                          <div key={idx} className={`flex items-center gap-3 ${isEven ? 'lg:flex-row-reverse lg:justify-end' : ''}`}>
                            <CheckCircle size={20} className="text-[#8BC34A] flex-shrink-0" />
                            <span className="text-gray-700">{highlight}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Icon Center */}
                  <div className="flex-shrink-0">
                    <div className={`relative w-32 h-32 rounded-full bg-gradient-to-r ${step.color} flex items-center justify-center shadow-2xl`}>
                      <div className="absolute inset-0 rounded-full bg-gradient-to-r ${step.color} animate-ping opacity-20"></div>
                      <IconComponent size={48} className="text-white relative z-10" />
                    </div>
                  </div>

                  {/* Empty Side for Balance */}
                  <div className="flex-1 hidden lg:block"></div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="mt-20 text-center">
          <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-3xl p-12 border-2 border-gray-200">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Ready to Start Your Journey?
            </h3>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Book a free consultation and let us create a personalized roadmap to your dream destination.
            </p>
            <button 
              onClick={() => {
                const element = document.getElementById('consultation');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="bg-gradient-to-r from-[#FF7043] to-[#F4511E] text-white px-10 py-5 rounded-full font-bold text-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 inline-flex items-center gap-3 group"
            >
              Book Free Consultation
              <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessTimeline;
